import { makeAutoObservable, runInAction } from 'mobx';
import {
  type ComputerReleaseDraft,
  type ComputerReleaseNotesApi,
  computerReleaseDraftErrorMessage,
  releaseDraftToUpdate,
} from '../services/computerReleaseNotesClient.ts';

export class ComputerReleaseEditorStore {
  version = '';
  detail: ComputerReleaseDraft | null = null;
  title = '';
  content = '';
  loading = false;
  saving = false;
  publishing = false;
  error = '';
  notice = '';
  private api: ComputerReleaseNotesApi;
  private onSaved: (draft: ComputerReleaseDraft) => void;
  private read: AbortController | null = null;
  private mutation: AbortController | null = null;
  private pendingPublication: {
    version: string;
    revision: string;
    requestId: string;
  } | null = null;

  constructor(
    api: ComputerReleaseNotesApi,
    onSaved: (draft: ComputerReleaseDraft) => void,
  ) {
    this.api = api;
    this.onSaved = onSaved;
    makeAutoObservable<
      this,
      'api' | 'onSaved' | 'read' | 'mutation' | 'pendingPublication'
    >(this, {
      api: false,
      onSaved: false,
      read: false,
      mutation: false,
      pendingPublication: false,
    });
  }

  get busy() {
    return this.saving || this.publishing;
  }
  get revision() {
    return this.detail?.editorial?.revision ?? '0';
  }
  get dirty() {
    if (!this.detail) return false;
    const saved = releaseDraftToUpdate(this.detail);
    return saved.title !== this.title || saved.content !== this.content;
  }
  get validationError() {
    if (!this.title.trim() || !this.content.trim())
      return '请填写标题和更新内容。';
    if (Array.from(this.title.trim()).length > 120)
      return '请将标题编辑到 120 字以内。';
    if (Array.from(this.content.trim()).length > 8000)
      return '请将更新内容编辑到 8000 字以内。';
    return '';
  }
  get canSave() {
    return Boolean(
      this.detail &&
        this.detail.publicationStatus !== 'unknown' &&
        !this.busy &&
        !this.validationError &&
        (this.dirty || !this.detail.editorial),
    );
  }
  get canPublish() {
    return Boolean(
      this.detail?.editorial &&
        this.detail.publicationStatus !== 'unknown' &&
        this.detail.publicationStatus !== 'published' &&
        !this.dirty &&
        !this.busy &&
        !this.validationError,
    );
  }

  setTitle(value: string) {
    this.title = value;
    this.notice = '';
  }
  setContent(value: string) {
    this.content = value;
    this.notice = '';
  }

  async open(version: string) {
    if (this.busy) return;
    this.read?.abort();
    const request = new AbortController();
    this.read = request;
    this.version = version;
    this.detail = null;
    this.title = '';
    this.content = '';
    this.loading = true;
    this.error = '';
    this.notice = '';
    try {
      const draft = await this.api.get(version, request.signal);
      runInAction(() => {
        if (this.read !== request || request.signal.aborted) return;
        this.accept(draft);
        this.onSaved(draft);
      });
    } catch (cause) {
      runInAction(() => {
        if (this.read === request && !request.signal.aborted)
          this.error = computerReleaseDraftErrorMessage(cause);
      });
    } finally {
      runInAction(() => {
        if (this.read === request) {
          this.loading = false;
          this.read = null;
        }
      });
    }
  }

  async save() {
    if (!this.canSave) return false;
    const version = this.version;
    const request = new AbortController();
    this.mutation = request;
    this.saving = true;
    this.error = '';
    this.notice = '';
    try {
      const draft = await this.api.save(
        version,
        {
          expectedRevision: this.revision,
          title: this.title.trim(),
          content: this.content.trim(),
        },
        request.signal,
      );
      if (this.mutation !== request || request.signal.aborted) return false;
      runInAction(() => {
        this.accept(draft);
        this.onSaved(draft);
        this.notice = '草稿已保存，重新打开后可继续编辑。';
      });
      return true;
    } catch (cause) {
      runInAction(() => {
        if (this.mutation === request && !request.signal.aborted)
          this.error = computerReleaseDraftErrorMessage(cause);
      });
      return false;
    } finally {
      runInAction(() => {
        if (this.mutation === request) {
          this.saving = false;
          this.mutation = null;
        }
      });
    }
  }

  async publish() {
    if (!this.canPublish) return false;
    const version = this.version;
    const revision = this.revision;
    if (
      this.pendingPublication?.version !== version ||
      this.pendingPublication.revision !== revision
    ) {
      this.pendingPublication = {
        version,
        revision,
        requestId: crypto.randomUUID(),
      };
    }
    const request = new AbortController();
    this.mutation = request;
    this.publishing = true;
    this.error = '';
    this.notice = '';
    try {
      const draft = await this.api.publish(
        version,
        {
          expectedRevision: revision,
          requestId: this.pendingPublication.requestId,
        },
        request.signal,
      );
      if (this.mutation !== request || request.signal.aborted) return false;
      runInAction(() => {
        this.accept(draft);
        this.onSaved(draft);
        this.notice = '更新消息已发布到指定测试账号。';
      });
      return true;
    } catch (cause) {
      runInAction(() => {
        if (this.mutation === request && !request.signal.aborted) {
          this.error = `${computerReleaseDraftErrorMessage(cause)} 发布结果请以重新读取的记录为准，保持内容不变重试不会重复发送同一修订。`;
        }
      });
      return false;
    } finally {
      runInAction(() => {
        if (this.mutation === request) {
          this.publishing = false;
          this.mutation = null;
        }
      });
    }
  }

  private accept(draft: ComputerReleaseDraft) {
    this.detail = draft;
    const editable = releaseDraftToUpdate(draft);
    this.title = editable.title;
    this.content = editable.content;
  }

  close() {
    if (!this.busy) {
      this.dispose();
      this.version = '';
      this.detail = null;
    }
  }

  dispose() {
    this.read?.abort();
    this.mutation?.abort();
    this.read = null;
    this.mutation = null;
    this.loading = false;
    this.saving = false;
    this.publishing = false;
  }
}
